import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/site/SiteShell";
import { ServiceDetailSection } from "@/components/site/ServiceDetailSection";
import { SERVICE_DETAIL_CONTENT } from "@/lib/service-detail-content";
import { jsonLdScript } from "@/lib/jsonld";
import { webPageScript } from "@/lib/webpage-jsonld";
import { ExternalLink, BadgeCheck, Download } from "lucide-react";

const TITLE = "TIN সার্টিফিকেট কি, কেন প্রয়োজন ও কিভাবে ডাউনলোড করবেন";
const DESC = "TIN certificate কী, কোন কোন ক্ষেত্রে প্রয়োজন এবং e-Return থেকে কিভাবে ডাউনলোড করবেন তার সম্পূর্ণ গাইড।";
const URL = "https://onlinetaxvat.com/tin-certificate-bd";

const USES = [
  "ব্যাংক লোন ও ক্রেডিট কার্ড আবেদন",
  "গাড়ি রেজিস্ট্রেশন ও মালিকানা পরিবর্তন",
  "ট্রেড লাইসেন্স ইস্যু ও নবায়ন",
  "সরকারি টেন্ডার ও ঠিকাদারি কাজে অংশগ্রহণ",
  "জমি/ফ্ল্যাট রেজিস্ট্রেশন",
  "সঞ্চয়পত্র ক্রয় ও আমদানি-রপ্তানি লাইসেন্স",
];

export const Route = createFileRoute("/tin-certificate-bd")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      webPageScript({ path: "/tin-certificate-bd", name: TITLE, description: DESC }),
      jsonLdScript({
        "@context": "https://schema.org",
        "@type": "Article",
        headline: "TIN সার্টিফিকেট কি এবং কেন প্রয়োজন",
        description: DESC,
        inLanguage: "bn",
        mainEntityOfPage: URL,
        author: { "@type": "Organization", name: "Online Tax VAT Bangladesh" },
        publisher: { "@type": "Organization", name: "Online Tax VAT Bangladesh" },
      }),
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SiteShell>
      <article className="container-page max-w-3xl py-14 md:py-20">
        <header className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-4">
            <BadgeCheck className="h-4 w-4" /> TIN Certificate Bangladesh
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold leading-tight">
            TIN সার্টিফিকেট কি এবং কেন প্রয়োজন
          </h1>
        </header>

        <section className="glass rounded-3xl border border-border/60 p-6 md:p-8 shadow-card space-y-4 text-[15px] leading-relaxed">
          <p>
            <strong>TIN (Taxpayer Identification Number)</strong> হলো একজন করদাতার পরিচয় নিশ্চিতকারী ইউনিক নম্বর, যা আয়কর সংক্রান্ত সকল কার্যক্রমে ব্যবহৃত হয়। NBR এই নম্বর ইস্যু করে থাকে এবং প্রতিটি করদাতার জন্য এটি স্থায়ী।
          </p>
        </section>

        <section className="mt-8">
          <h2 className="font-display text-xl md:text-2xl font-bold mb-4">TIN Certificate কোথায় প্রয়োজন হয়</h2>
          <ul className="grid sm:grid-cols-2 gap-3">
            {USES.map((u, i) => (
              <li key={i} className="flex items-start gap-3 p-4 rounded-xl border border-border/60 bg-card">
                <span className="mt-0.5 h-7 w-7 shrink-0 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold">{i + 1}</span>
                <span className="text-sm leading-relaxed">{u}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-8 rounded-2xl border border-primary/20 bg-primary/5 p-6">
          <div className="flex items-start gap-3">
            <span className="h-9 w-9 shrink-0 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
              <Download className="h-5 w-5" />
            </span>
            <div>
              <h2 className="font-display font-bold text-lg mb-2">কিভাবে ডাউনলোড করবেন</h2>
              <p className="text-sm leading-relaxed">
                e-Return সিস্টেমে রেজিস্ট্রেশনের পর যেকোনো সময় TIN certificate ও Tax certificate ডাউনলোড/প্রিন্ট করতে পারবেন <strong>"Tax record"</strong> মেনু থেকে। PDF আকারে সংরক্ষণ করে যেকোনো প্রতিষ্ঠানে জমা দিতে পারবেন।
              </p>
            </div>
          </div>
        </section>

        <section className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 p-6 rounded-2xl border border-border/60 bg-card">
          <div>
            <h3 className="font-display font-bold">আরও জানুন</h3>
            <Link to="/knowledge-hub/$slug" params={{ slug: "faq-e-return" }} className="text-primary hover:underline text-sm">
              → e-Return রেজিস্ট্রেশন ও ফাইলিং সম্পূর্ণ FAQ
            </Link>
          </div>
          <a
            href="https://etaxnbr.gov.bd"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 transition shadow-md"
          >
            etaxnbr.gov.bd এ যান <ExternalLink className="h-4 w-4" />
          </a>
        </section>
        <ServiceDetailSection content={SERVICE_DETAIL_CONTENT["tin-certificate-bd"]} />
      </article>
    </SiteShell>
  );
}
